import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Search,
  CreditCard,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  FileText,
  DollarSign,
  Loader2,
  Calendar,
  User,
  Building,
  Tag,
  X,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "../../components/SEO";
import { trackPurchase, trackAdsFoundationsFirstConversion } from "../../utils/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

const ProgramPayment = () => {
  const [searchParams] = useSearchParams();
  const success = searchParams.get("success");
  const cancelled = searchParams.get("cancelled");
  const submissionIdFromUrl = searchParams.get("submission_id");
  const prefillId = searchParams.get("submission_id");

  const [submissionId, setSubmissionId] = useState(prefillId || "");
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState("");
  const [discountLoading, setDiscountLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [finalAmount, setFinalAmount] = useState(null);

  useEffect(() => {
    if (cancelled) {
      toast.error("Payment was cancelled. You can try again when ready.");
    }
    if (success && submissionIdFromUrl) {
      // Track successful purchase
      trackPurchase(submissionIdFromUrl, 0, 'Program Enrollment');
      // Check if Foundations First and fire Google Ads conversion
      fetch(`${API}/api/programs/application/${submissionIdFromUrl}`)
        .then(res => res.json())
        .then(data => {
          if (data.program_type === 'foundations_first') {
            trackAdsFoundationsFirstConversion(submissionIdFromUrl);
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelled, success, submissionIdFromUrl]);

  useEffect(() => {
    if (prefillId && !success) {
      handleLookup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillId]);

  const handleLookup = async (e) => {
    if (e) e.preventDefault();
    
    if (!submissionId.trim()) {
      toast.error("Please enter your application number");
      return;
    }

    setLoading(true);
    setError(null);
    setApplication(null);
    setAppliedDiscount(null);
    setFinalAmount(null);
    setDiscountCode("");

    try {
      const response = await fetch(`${API}/api/programs/lookup/${submissionId.trim()}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Application not found");
      }

      setApplication(data);
      setFinalAmount(data.amount_due);
      
      // If discount was already applied to the application
      if (data.discount_applied) {
        setAppliedDiscount(data.discount_applied);
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      toast.error("Please enter a discount code");
      return;
    }

    setDiscountLoading(true);
    try {
      const params = new URLSearchParams({
        code: discountCode.trim().toUpperCase(),
        program_type: application.program_type,
        amount: application.amount_due.toString()
      });
      
      const response = await fetch(`${API}/api/programs/validate-discount?${params}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Invalid discount code");
      }

      // Calculate the discount amount based on type
      let discountAmount = 0;
      let newFinalAmount = application.amount_due;
      
      if (data.discount_type === "percentage") {
        discountAmount = (application.amount_due * data.discount_value) / 100;
        newFinalAmount = application.amount_due - discountAmount;
      } else if (data.discount_type === "fixed") {
        discountAmount = Math.min(data.discount_value, application.amount_due);
        newFinalAmount = application.amount_due - discountAmount;
      }
      
      // Ensure we don't go below 0
      newFinalAmount = Math.max(0, newFinalAmount);

      setAppliedDiscount({
        code: discountCode.trim().toUpperCase(),
        discount_type: data.discount_type,
        discount_value: data.discount_value,
        discount_amount: discountAmount
      });
      setFinalAmount(newFinalAmount);
      toast.success(`Discount applied! You saved $${discountAmount.toLocaleString()}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setFinalAmount(application.amount_due);
    setDiscountCode("");
    toast.info("Discount removed");
  };

  const handlePayment = async () => {
    if (!application) return;

    setPaymentLoading(true);
    try {
      // Build URL with discount code if applied
      let url = `${API}/api/programs/pay/${application.submission_id}`;
      if (appliedDiscount?.code) {
        url += `?discount_code=${encodeURIComponent(appliedDiscount.code)}`;
      }
      
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to create payment session");
      }

      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.message);
      setPaymentLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rnb-light to-white">
        <SEO title="Payment Successful - RNB Consulting Group" />
        <div className="max-w-2xl mx-auto px-4 py-20">
          <Card className="border-0 shadow-xl">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-rnb-navy mb-4">
                Payment Successful!
              </h1>
              <p className="text-rnb-gray text-lg mb-6">
                Thank you for your payment. Your enrollment is now confirmed!
              </p>
              <p className="text-rnb-gray mb-8">
                You'll receive a confirmation email shortly with next steps and your login credentials
                for the client portal.
              </p>
              <Link to="/">
                <Button size="lg" className="bg-rnb-blue hover:bg-rnb-blue/90">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Return to Homepage
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rnb-light to-white">
      <SEO 
        title="Complete Your Program Payment - RNB Consulting Group"
        description="Enter your application number to complete payment for your RNB Consulting Group program enrollment."
      />

      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center text-rnb-blue hover:text-rnb-navy">
            <ArrowLeft className="w-4 h-4 mr-2" />
            RNB Consulting Group
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
            Complete Your Program Payment
          </h1>
          <p className="text-rnb-gray text-lg">
            Enter your application number to look up your enrollment and complete payment
          </p>
        </div>

        {/* Lookup Form */}
        {!application && (
          <Card className="max-w-xl mx-auto border-0 shadow-lg mb-8">
            <CardContent className="p-8">
              <form onSubmit={handleLookup}>
                <Label htmlFor="submissionId" className="text-rnb-navy font-medium">
                  Application Number
                </Label>
                <p className="text-sm text-rnb-gray mb-3">
                  Enter the application number from your confirmation email (e.g., FF-2026-0001)
                </p>
                <div className="flex gap-3">
                  <Input
                    id="submissionId"
                    type="text"
                    placeholder="FF-2026-0001"
                    value={submissionId}
                    onChange={(e) => setSubmissionId(e.target.value.toUpperCase())}
                    className="flex-1 font-mono text-lg tracking-wider"
                    data-testid="submission-id-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="bg-rnb-blue hover:bg-rnb-blue/90"
                    data-testid="lookup-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Look Up
                      </>
                    )}
                  </Button>
                </div>
              </form>

              {error && (
                <div className="mt-4 p-4 bg-red-50 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-700 font-medium">Application Not Found</p>
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Application Details & Payment */}
        {application && (
          <div className="grid md:grid-cols-2 gap-8">
            {/* Application Details */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-rnb-light rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-rnb-blue" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-rnb-navy">
                      Application Details
                    </h2>
                    <p className="text-sm text-rnb-gray font-mono">
                      {application.submission_id}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3 py-3 border-b">
                    <User className="w-5 h-5 text-rnb-gray mt-0.5" />
                    <div>
                      <p className="text-sm text-rnb-gray">Applicant</p>
                      <p className="font-medium text-rnb-navy">{application.applicant_name}</p>
                      <p className="text-sm text-rnb-gray">{application.applicant_email}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 py-3 border-b">
                    <Building className="w-5 h-5 text-rnb-gray mt-0.5" />
                    <div>
                      <p className="text-sm text-rnb-gray">Program</p>
                      <p className="font-medium text-rnb-navy">{application.program_name}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 py-3 border-b">
                    <Calendar className="w-5 h-5 text-rnb-gray mt-0.5" />
                    <div>
                      <p className="text-sm text-rnb-gray">Applied On</p>
                      <p className="font-medium text-rnb-navy">
                        {new Date(application.created_at).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                  </div>

                  {application.selected_addons?.length > 0 && (
                    <div className="py-3 border-b">
                      <p className="text-sm text-rnb-gray mb-2">Add-ons Selected</p>
                      <ul className="space-y-1">
                        {application.selected_addons.map((addon, idx) => (
                          <li key={addon.name || addon.title || i} className="text-sm text-rnb-navy flex justify-between">
                            <span>{addon.name}</span>
                            <span className="font-medium">${addon.price?.toLocaleString()}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {application.discount_applied && (
                    <div className="py-3 border-b">
                      <p className="text-sm text-rnb-gray mb-1">Discount Applied</p>
                      <p className="text-green-600 font-medium">
                        {application.discount_applied.code} (-${application.discount_applied.discount_amount?.toLocaleString()})
                      </p>
                    </div>
                  )}
                </div>

                {/* Total */}
                <div className="bg-rnb-light rounded-xl p-6 mt-6">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-medium text-rnb-navy">Amount Due</span>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-6 h-6 text-rnb-blue" />
                      <span className="font-mono text-3xl font-bold text-rnb-navy">
                        {application.amount_due?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Change Application */}
                <button
                  onClick={() => setApplication(null)}
                  className="mt-4 text-sm text-rnb-blue hover:underline"
                >
                  ← Look up a different application
                </button>
              </CardContent>
            </Card>

            {/* Payment Section */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h2 className="font-heading text-xl font-bold text-rnb-navy mb-6">
                  Complete Payment
                </h2>

                {/* Discount Code Section */}
                <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                  <Label className="text-sm font-medium text-rnb-navy flex items-center gap-2 mb-3">
                    <Tag className="w-4 h-4" />
                    Have a discount code?
                  </Label>
                  
                  {!appliedDiscount ? (
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="Enter code"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        className="flex-1 font-mono"
                        data-testid="discount-code-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleApplyDiscount}
                        disabled={discountLoading || !discountCode.trim()}
                        data-testid="apply-discount-btn"
                      >
                        {discountLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          "Apply"
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-mono font-medium text-green-700">{appliedDiscount.code}</p>
                          <p className="text-sm text-green-600">
                            -${appliedDiscount.discount_amount?.toLocaleString()} off
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleRemoveDiscount}
                        className="text-gray-400 hover:text-gray-600"
                        title="Remove discount"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Price Summary */}
                <div className="mb-6 space-y-2">
                  <div className="flex justify-between text-rnb-gray">
                    <span>Subtotal</span>
                    <span>${application.amount_due?.toLocaleString()}</span>
                  </div>
                  {appliedDiscount && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({appliedDiscount.code})</span>
                      <span>-${appliedDiscount.discount_amount?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold text-rnb-navy pt-2 border-t">
                    <span>Total</span>
                    <span>${(finalAmount || application.amount_due)?.toLocaleString()}</span>
                  </div>
                </div>

                <Button
                  onClick={handlePayment}
                  disabled={paymentLoading}
                  className="w-full py-6 text-lg bg-rnb-teal hover:bg-rnb-teal/90"
                  data-testid="pay-program-btn"
                >
                  {paymentLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay ${(finalAmount || application.amount_due)?.toLocaleString()} Now
                    </>
                  )}
                </Button>

                {/* Security Note */}
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-rnb-gray text-center">
                    🔒 Your payment is processed securely by Stripe. 
                    RNB Consulting Group never stores your payment details.
                  </p>
                </div>

                {/* Help */}
                <div className="mt-6 text-center">
                  <p className="text-sm text-rnb-gray">
                    Questions? Contact us at{" "}
                    <a href="mailto:info@rnbconsultinggroup.com" className="text-rnb-blue hover:underline">
                      info@rnbconsultinggroup.com
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-12 text-center">
          <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-2">
            Can't find your application number?
          </h3>
          <p className="text-rnb-gray mb-4">
            Check your email for a message from RNB Consulting Group with the subject 
            "Application Received". Your application number is displayed prominently in that email.
          </p>
          <p className="text-rnb-gray">
            Still need help?{" "}
            <a href="mailto:info@rnbconsultinggroup.com" className="text-rnb-blue hover:underline">
              Contact us
            </a>{" "}
            or call{" "}
            <a href="tel:8882504605" className="text-rnb-blue hover:underline">
              (888) 250-4605
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default ProgramPayment;
